import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './PrivacyPolicy.module.css';

const PrivacyPolicy = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  // prettier-ignore
  return (
    <div className={classes}>
      <nav class="privacy-toc">
        <ul>
          <li><a href="#what-is-this">What is this?</a></li>
          <li><a href="#personal-data">Personal data</a></li>
          <li><a href="#your-data-and-how-and-why-we-process-it">How and why we process your data</a></li>
          <li><a href="#why-we-share-your-data">Why we share your data</a></li>
          <li><a href="#how-we-store-your-data">How we store your data</a></li>
          <li><a href="#how-long-we-keep-your-data">How long we keep your data</a></li>
          <li><a href="#your-rights-as-a-data-subject">Your rights as a data subject</a></li>
          <li><a href="#website-cookies">Website Cookies</a></li>
        </ul>
      </nav>
      <p className={css.lastUpdated}>Last updated: 25th October 2025</p>
      <h2 id="what-is-this">What is this?</h2>
        <p>At Signalise Co-op we collect and use information about our members, clients, service users, and communication professionals. That information is known as ‘personal data’. We have a legal responsibility to handle that information with respect, and to tell you how and why we use your data. 
        This privacy notice explains what we do with your data, and also explains the rights you have over your data. These include amongst others, the right to ask for copies of your data, and to object to the way your data is being used. 
        If you have any questions about this privacy notice or anything related to personal data, you can contact our Business and Development Manager at <a href='mailto:jen@signalise.coop'>jen@signalise.coop</a> or on <a href="tel:01518080373">0151 808 0373</a>. </p>
      <h2 id="personal-data">Personal data</h2>
        <p>When we say ‘personal data’ this means any information that relates to a living person who can be identified from that information. This will usually include your name, address, contact details, and other information we collect as part of our relationship with you, which includes anyone we come into contact with through our work.
        Some especially sensitive information is known as ‘special categories’ of data, and this includes information about a person’s physical or mental health, religious beliefs, sex life or sexual orientation. There are very strict legal rules around using these types of data, or information about criminal offences.
        If we collect any special categories data about you we will always be clear about why we are collecting it. We only use your personal data if we need to for a specific purpose, as explained below. Most often, we collect your personal data directly from you, through our contact with you.</p>
      <h2 id="your-data-and-how-and-why-we-process-it">Your data and how and why we process it</h2>
        <p>In general terms, we use your data so we can manage and support our relationship with you, comply with legal obligations, improve our services and achieve our legitimate business aims. The information below gives more details about our purposes for processing data, and the legal bases for our processing.</p>
      <h3>Clients:</h3>
        <p>We collect and process information including your name, contact details and payments details so that we can enter into an agreement for service provision and manage the relationship between us. We do this on the legal basis that it is necessary for contract purposes.</p>
      <h3>Service Users:</h3>
        <p>We process your name, contact details including address, mobile telephone number and email address, your GP’s contact details if you choose to provide them to help communication with your GP, and your preferred communication professionals if you have them. We will collect other information through our ongoing contact with you as we work together.</p>
        <p>If we have an agreement directly with you, we process this data about you on the legal basis of it being necessary for our contract or agreement.
        If we provide services to you through an agreement with another party, such as your GP or hospital, this is on the legal basis of the processing of your data being necessary for our legitimate interests of being able to provide and improve our communication professional services as appropriate.</p>
        <p>We collect information related to your health and any criminal information related to you or anyone closely connected to you if applicable, so we can assess the risk and identify anything that could affect the safety of our communication professionals, or the way we are able to provide you with the service.
        We have a legal responsibility to protect the safety of everyone involved in our work and have a lawful right to process data as explained, when it is for the purposes of safety and wellbeing.</p>
        <p>We do not share any health, criminal or other sensitive information outside of Signalise Co-op without your consent. The only exception would be if we need to report something to an external agency in order to protect the wellbeing of yourself or someone else.
        This includes social services referrals or contacting the emergency services if the situation required us to.</p>
      <h3>Communication Professionals:</h3>
        <p>We process your name, gender, pronouns, date of birth, contact details, your professional experience, registration details and qualifications, professional insurance, your photo for your profile on the platform if you are happy to provide it, and we check your ID and right to work in the UK.
        We also process other information that we collect through our ongoing contact with you as we work together. </p>
        <p>We use this data on the legal basis that it is necessary for us to be able to provide and improve our services as appropriate, for entering into and managing our agreement with you to provide services.</p>
        <p>We collect information related to your health and any criminal information, including carrying out a DBS check, so we can manage any risks and identify anything that could affect your safety or of our clients.
        We have a legal responsibility to protect the safety of everyone involved in our work and have a lawful right to process data as explained, when it is for the purposes of safety and wellbeing.</p>
        <p>We collect ethnicity based only on your explicit consent, if you choose to provide that information, so we can allow clients and service users to select communication professionals by ethnicity if it is appropriate to do so, for example if it is a culturally sensitive event.
        This is optional and there is no negative impact if you choose not to provide this information. </p>
        <p>We do not share any health, criminal or other sensitive information outside of Signalise without your consent. The only exception would be if we need to report something to an external agency in order to protect the wellbeing of yourself or someone else.
        This includes social services referrals or contacting the emergency services if the situation required us to.</p>
        <p>We will ask you for referees, so please ensure your referees have consented to you sharing their name and contact details with us for our purposes of taking up references.</p>
      <h3>Members:</h3>
        <p>We collect your name, contact details, and payment transaction details when you apply for membership, on the legal basis that it is necessary for us to manage your membership. 
        We will ask you to declare any conflicts of interest if applicable.</p>
      <h3>People contacting us via our website:</h3>
        <p>If you fill out our online contact form or use our live chat function, we will collect and process your name and contact details and nature of your enquiry on the legal basis that it is necessary for our legitimate interests of managing enquiries that come in through those channels.</p>
        <p>Please note that if you choose to log in through an existing social media account, this processing is subject to the relevant social media company’s own Privacy Notice.</p>
      <h3>Other contacts:</h3>
        <p>We process names, contact details, and other information produced through our interactions, of other external contacts, such as our suppliers, to enable us to manage our working relationship with you, on the basis of our legitimate interests to be able to provide our services to those who need them, in the most effective way. 
        You have the right to object to any of this processing and we will assess any objection sensitively.</p>
      <h3>Marketing messages:</h3>
        <p>If we send electronic information about our services, fundraising or similar to you as an individual (rather than a business representative), we will do so only on the basis of your freely-given consent (or where you are an existing client and have not opted out of receiving these messages) and you can opt-out at any time. </p>
        <p>If we send electronic marketing messages to you as a business or external agency representative, we will do so for the purposes of our legitimate interests to increase awareness of our services, and you can opt-out at any time. </p>
        <p>If we send hard copy marketing messages to you, we will do so for the purposes of our legitimate interests to increase awareness of our services, and you can opt-out at any time.
        </p>
      <h2 id="why-we-share-your-data">Why we share your data</h2>
        <p>We share the data we process with other organisations, only when we have a lawful basis to do so, or when we are using a supplier who will act as a ‘Data Processor’ on our behalf. ‘Processors’ are businesses who handle, or could potentially handle, personal data as part of providing a service to us, and include our IT system providers, our email providers, and our website hosts.</p>
        <p>Other organisations we share data with include the HMRC and banks for processing tax and payments, and we will co-operate with police and other authorities if we are asked to, in order to investigate or prevent crime, including fraud, and other unwanted behaviours such as incompetence in public roles.</p>
        <p>We would also report something to an external agency in order to protect the wellbeing of yourself or someone else. This includes social services referrals or contacting the emergency services if the situation required us to.</p>
      <h2 id="how-we-store-your-data">How we store your data </h2>
        <p>Your personal data is held in both hard copy and electronic formats.
        Where we store or transfer your data outside of the UK, we do so only
        where appropriate safeguards are in place, including the data being in a
        country that has been assessed as ‘adequate’ or we have entered into
        Standard Contractual Clauses and have appropriate safeguards in place to
        control the protection of your data.</p>
      <h2 id="how-long-we-keep-your-data">How long we keep your data </h2>
        <p>Your data is only kept for as long as there is a lawful reason to retain it. Some of our retention periods are based on legal requirements, and others are based on the practical reasons we need to keep the data for a certain period of time. </p>
        <p>Once we reach the retention period, we will securely delete the relevant data, unless we are legally required to keep it longer, or there are legal reasons why we should keep it longer.</p>
      <h2 id="your-rights-as-a-data-subject">Your rights as a data subject </h2>
        <p>As a data subject, you have the following rights in relation to your personal data: </p>
          <ul className={css.listContainer}>
            <li>To be informed about how and why your data is handled, which we do so through this Privacy Notice;</li>
            <li>To gain access to copies of your personal data; </li>
            <li>To have errors or inaccuracies in your data changed;</li>
            <li>To have your personal data erased, in limited circumstances (sometimes known as the ‘right to be forgotten’);</li>
            <li>To object to the processing of your personal data for marketing purposes or when the processing is based on the public interest or other legitimate interests; </li>
            <li>To restrict the processing of your personal data, in limited circumstances; </li>
            <li>To obtain a copy of some of your data in a commonly used electronic form, in limited circumstances;</li>
            <li>Rights around how you are affected by any profiling or automated decisions – we do not currently make any automated decisions about our clients, volunteers or partners.</li>
          </ul>
        <p>If you wish to exercise any of these rights, please contact us. </p>
        <p>For more information about these rights, please see the<a href="https://ico.org.uk/"> ICO’s website</a> or contact us.</p>
      <h2 id="withdrawing-consent">Withdrawing consent </h2>
        <p>If we are relying on your consent to process your data, you may withdraw your consent at any time by contacting us.</p>
      <h2 id="complaints-to-the-information-commissioner">Complaints to the Information Commissioner </h2>
        <p>You have a right to complain to the Information Commissioner's Office (ICO) about the way in which we process your personal data.
        You can make a complaint on the <a href="https://ico.org.uk/"> ICO’s website</a>.</p>
      <h2 id="website-cookies">Website Cookies </h2>
        <h3 id="our-website-uses-both-essential-and-non-essential-cookies.">Our
          website uses both essential and non-essential cookies.</h3>
          <p>We use cookies to make our website work properly and to help us
          understand how people use it. Some cookies are essential and cannot be
          switched off, while others are optional and only used if you
          consent.</p>
          <p>You can manage your cookie preferences at any time through our cookie
          banner or settings tool.</p>
          <h3 id="essential-cookies">Essential cookies</h3>
          <p>These cookies are required for core site functionality such as
          security, login sessions, and our chat system. Without them, the site
          won’t function correctly.</p>
          <p>They include:</p>
          <ul>
          <li>
          <p>Login and session cookies for secure account access</p>
          </li>
          <li>
          <p>Cookies used to detect bots and protect the site from abuse (e.g.
          __cf_bm, rc::a, rc::c)</p>
          </li>
          <li>
          <p>Consent management cookies (e.g. CookieConsent)</p>
          </li>
          <li>
          <p>Cookies required for embedded documentation pages (GitBook session
          cookies)</p>
          </li>
          <li>
          <p>Functional error-handling cookie (st-autherror)</p>
          </li>
          </ul>
          <p>These do not track you for advertising.</p>
          <h3 id="analytics-cookies-optional">Analytics cookies (optional)</h3>
          <p>With your consent, we use Google Analytics to collect anonymised
          information about how visitors use our site.</p>
          <p>This helps us improve our services and content. Analytics cookies
          (e.g. _ga, _ga_#) track things like:</p>
          <ul>
          <li>
          <p>Which pages are visited</p>
          </li>
          <li>
          <p>How long users spend on the site</p>
          </li>
          <li>
          <p>Basic technical information about the device used</p>
          </li>
          </ul>
          <p>We do not use Google Analytics to identify you personally, and we do
          not combine analytics data with other information about you.</p>
          <p>You may block these cookies, and you can also install the Google
          Analytics Opt-Out Browser Add-On if you prefer.</p>
          <h3 id="video-social-media-cookies-optional">Video &amp; social media
          cookies (optional)</h3>
          <p>We embed content from third-party services such as YouTube and
          X/Twitter. If you choose to interact with these features (e.g. by
          playing a video), they may set cookies that track your activity for
          service performance and, in some cases, personalised advertising
          purposes.</p>
          <p>If you do not want this tracking, please do not play embedded videos
          or interact with social media embeds.</p>
          <p>Cookies in this category include:</p>
          <ul>
          <li>
          <p>YouTube cookies and storage keys (e.g. yt.innertube::nextId,
          YtIdbMeta#databases, LAST_RESULT_ENTRY_KEY)</p>
          </li>
          <li>
          <p>X/Twitter embed tracking (e.g. i/jot/embeds)</p>
          </li>
          <li>
          <p>Maps / location cookies (optional)</p>
          </li>
          <li>
          <p>Where Google Maps is used, cookies may be set to allow map features
          to function</p>
          </li>
          <li>
          <p>The x-gitbook-theme cookie stores your display settings for embedded
          GitBook pages (for example, light/dark mode). It supports the proper
          display of our documentation and does not track you. This cookie is
          required for functionality.</p>
          </li>
          </ul>
          <h3 id="updates">Updates</h3>
          <p>Our site is regularly scanned for cookies. New or changed cookies
          will be listed here, and the policy will be updated to reflect those
          changes.</p>
          <h3 id="cookie-table">Cookie table</h3>
          <h3 id="essential-cookies-1">Essential cookies</h3>
          <p className={css.tableScroll}>
            <table className={css.table}>
            <thead>
            <tr  className={css.header}>
            <th><strong>Cookie</strong></th>
            <th><strong>Provider</strong></th>
            <th><strong>Purpose</strong></th>
            <th><strong>Expiry</strong></th>
            </tr>
            <tr className={css.odd}>
            <th>__cf_bm</th>
            <th>Twitter</th>
            <th>Bot/security protection for embedded X content</th>
            <th>1 day</th>
            </tr>
            <tr className={css.even}>
            <th>__gitbook_session</th>
            <th>GitBook</th>
            <th>Maintains session for embedded docs</th>
            <th>Persistent</th>
            </tr>
            <tr className={css.odd}>
            <th>__session</th>
            <th>GitBook</th>
            <th>Maintains access to embedded docs</th>
            <th>400 days</th>
            </tr>
            <tr className={css.even}>
            <th>1.gif</th>
            <th>Cookiebot</th>
            <th>Ensures cookie consent manager functions</th>
            <th>Session</th>
            </tr>
            <tr className={css.odd}>
            <th>CookieConsent</th>
            <th>signalise.coop / organisation.signalise.coop</th>
            <th>Stores your cookie consent preferences</th>
            <th>1 year</th>
            </tr>
            <tr className={css.even}>
            <th>rc::a</th>
            <th>Google</th>
            <th>Bot/security protection (reCAPTCHA)</th>
            <th>Persistent</th>
            </tr>
            <tr className={css.odd}>
            <th>rc::c</th>
            <th>Google</th>
            <th>Bot/security protection (reCAPTCHA)</th>
            <th>Session</th>
            </tr>
            <tr className={css.even}>
            <th>st-autherror</th>
            <th>signalise.coop</th>
            <th>Used to display login error message after failed sign-in</th>
            <th>Session</th>
            </tr>
            <tr className={css.odd}>
            <th>x-gitbook-theme</th>
            <th>GitBook</th>
            <th>Stores display settings (e.g. theme) for embedded docs</th>
            <th>1 day</th>
            </tr>
            </thead>
            <tbody>
            </tbody>
            </table>
          </p>
          <h2 id="analytics-performance-cookies">Analytics &amp; Performance
          cookies</h2>
          <p className={css.tableScroll}>
            <table className={css.table}>
            <thead>
            <tr  className={css.header}>
            <th><strong>Cookie</strong></th>
            <th><strong>Provider</strong></th>
            <th><strong>Purpose</strong></th>
            <th><strong>Expiry</strong></th>
            </tr>
            <tr className={css.odd}>
            <th>_ga</th>
            <th>Google Analytics</th>
            <th>Website usage stats (not personalised)</th>
            <th>2 years</th>
            </tr>
            <tr className={css.even}>
            <th>_ga_#</th>
            <th>Google Analytics</th>
            <th>Session/visit metrics</th>
            <th>2 years</th>
            </tr>
            </thead>
            <tbody>
            </tbody>
            </table>
          </p>
          <h3 id="video-maps-social-cookies">Video, Maps &amp; Social cookies</h3>
          <p className={css.tableScroll}>
            <table className={css.table}>
            <thead>
            <tr className={css.header}>
            <th><strong>Cookie</strong></th>
            <th><strong>Provider</strong></th>
            <th><strong>Purpose</strong></th>
            <th><strong>Expiry</strong></th>
            </tr>
            <tr className={css.odd}>
            <th>i/jot/embeds</th>
            <th>X/Twitter</th>
            <th>Track engagement with embedded posts/videos</th>
            <th>Session</th>
            </tr>
            <tr className={css.even}>
            <th>LAST_RESULT_ENTRY_KEY</th>
            <th>YouTube</th>
            <th>Tracks interaction with embedded video</th>
            <th>Session</th>
            </tr>
            <tr className={css.odd}>
            <th>nextId</th>
            <th>YouTube</th>
            <th>Tracks interaction with embedded video</th>
            <th>Session</th>
            </tr>
            <tr className={css.even}>
            <th>requests</th>
            <th>YouTube</th>
            <th>Tracks interaction with embedded video</th>
            <th>Session</th>
            </tr>
            <tr className={css.odd}>
            <th>yt.innertube::nextId</th>
            <th>YouTube</th>
            <th>Tracks videos viewed</th>
            <th>Persistent</th>
            </tr>
            <tr className={css.even}>
            <th>ytidb::LAST_RESULT_ENTRY_KEY</th>
            <th>YouTube</th>
            <th>Tracks interaction with embedded video</th>
            <th>Persistent</th>
            </tr>
            <tr className={css.odd}>
            <th>YtIdbMeta#databases</th>
            <th>YouTube</th>
            <th>Stores YouTube player data</th>
            <th>Persistent</th>
            </tr>
            <tr className={css.even}>
            <th>st-da861008-token</th>
            <th>Google Maps</th>
            <th>Enables map functionality</th>
            <th>180 days</th>
            </tr>
            </thead>
            <tbody>
            </tbody>
            </table>
          </p>
          <h3 id="how-to-change-cookie-settings">How to change cookie
          settings</h3>
          <p>You can manage your cookie preferences at any time through our cookie
          banner. You can also control cookies through your browser settings:</p>
          <ul>
          <li>
          <p><strong>Chrome:</strong> Settings → Privacy &amp; Security →
          Cookies</p>
          </li>
          <li>
          <p><strong>Firefox:</strong> Settings → Privacy &amp; Security → Cookies
          &amp; Site Data</p>
          </li>
          <li>
          <p><strong>Safari:</strong> Preferences → Privacy</p>
          </li>
          <li>
          <p>E<strong>dge:</strong> Settings → Cookies &amp; Site Permissions</p>
          </li>
          </ul>
          <p>To opt-out of Google Analytics everywhere, install the <strong>Google
          Analytics Opt-out Browser Add-on</strong>.</p>
          <h2 id="review">Review</h2>
          <p>Signalise Co-op will ensure that the Privacy Notice is accessible to
          staff and that key points are communicated to members and clients where
          relevant.</p>
          <p>Jennifer Bird will review this policy every two years, or sooner if
          there are changes in legislation or organisational need.</p>
    </div>
  );
};

const { string } = PropTypes;

PrivacyPolicy.propTypes = {
  rootClassName: string,
  className: string,
};

export default PrivacyPolicy;
